import { useState, useEffect, createContext, useContext } from 'react';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, googleProvider, functions } from '../firebase/config';

const syncUserOnLoginFn = httpsCallable(functions, 'syncUserOnLogin');

// Context per condividere lo stato auth
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let lastSyncedUid = null;
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);

      // Sync users/{uid} (role + lastLoginAt) una volta per sessione/uid.
      if (firebaseUser && firebaseUser.uid !== lastSyncedUid) {
        lastSyncedUid = firebaseUser.uid;
        syncUserOnLoginFn().catch((err) => {
          console.warn('syncUserOnLogin fallita:', err?.message || err);
        });
      } else if (!firebaseUser) {
        lastSyncedUid = null;
      }
    });
    return () => unsubscribe();
  }, []);

  // Google sign in
  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError(getErrorMessage(err.code));
      setLoading(false);
    }
  };

  // Email/password sign in
  const signInWithEmail = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error('Email sign-in error:', err);
      setError(getErrorMessage(err.code));
      setLoading(false);
      throw err;
    }
  };

  // Email/password sign up
  const signUpWithEmail = async (email, password, displayName) => {
    try {
      setError(null);
      setLoading(true);
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name if provided
      if (displayName && result.user) {
        await updateProfile(result.user, { displayName });
      }

      // Email di verifica: con email/password l'indirizzo non è provato, e il
      // gate degli accessi lo richiede. Se l'invio fallisce non si blocca la
      // registrazione — dalla schermata di attesa si può rinviare.
      if (result.user) {
        try {
          await sendEmailVerification(result.user);
        } catch (err) {
          console.warn('Invio email di verifica fallito:', err?.message || err);
        }
      }
    } catch (err) {
      console.error('Email sign-up error:', err);
      setError(getErrorMessage(err.code));
      setLoading(false);
      throw err;
    }
  };

  // Password reset
  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (err) {
      console.error('Password reset error:', err);
      setError(getErrorMessage(err.code));
      throw err;
    }
  };

  // Change email: reauth con password attuale, poi verifyBeforeUpdateEmail
  // (la mail cambia solo dopo che l'utente clicca il link sulla nuova casella)
  const changeEmail = async (newEmail, password) => {
    try {
      setError(null);
      if (!auth.currentUser || !auth.currentUser.email) {
        const err = new Error('Utente non autenticato.');
        err.code = 'auth/user-not-found';
        throw err;
      }
      const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await verifyBeforeUpdateEmail(auth.currentUser, newEmail);
    } catch (err) {
      console.error('Change email error:', err);
      throw err;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('Sign-out error:', err);
      setError(err.message);
    }
  };

  const value = {
    user,
    loading,
    error,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    changeEmail,
    signOut,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook per usare il context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Messaggi di errore user-friendly in italiano
function getErrorMessage(code) {
  switch (code) {
    // Google auth errors
    case 'auth/popup-closed-by-user':
      return 'Login annullato. Riprova quando vuoi.';
    case 'auth/popup-blocked':
      return 'Popup bloccato dal browser. Abilita i popup per questo sito.';
    case 'auth/cancelled-popup-request':
      return 'Operazione annullata.';
    case 'auth/network-request-failed':
      return 'Errore di rete. Controlla la connessione.';
    case 'auth/user-disabled':
      return 'Account disabilitato. Contatta il supporto.';
    case 'auth/operation-not-allowed':
      return 'Metodo di login non abilitato.';

    // Email/password errors
    case 'auth/email-already-in-use':
      return 'Questa email è già registrata. Prova ad accedere.';
    case 'auth/invalid-email':
      return 'Indirizzo email non valido.';
    case 'auth/weak-password':
      return 'Password troppo debole. Usa almeno 6 caratteri.';
    case 'auth/user-not-found':
      return 'Nessun account trovato con questa email.';
    case 'auth/wrong-password':
      return 'Password errata. Riprova.';
    case 'auth/invalid-credential':
      return 'Credenziali non valide. Controlla email e password.';
    case 'auth/too-many-requests':
      return 'Troppi tentativi. Riprova tra qualche minuto.';
    case 'auth/missing-password':
      return 'Inserisci la password.';

    default:
      return 'Errore durante l\'autenticazione. Riprova.';
  }
}
